/* istanbul ignore file */

import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

import { DomController } from '@ionic/angular';

import { PlatformService } from './ssr/platform.service';
import { WindowService } from './ssr/window.service';

const IMAGE_LOADING_DELAY = 180;
const VERTICAL_LAZY_LOADING_OFFSET_IN_PIXELS = 600;
const HORIZONTAL_LAZY_LOADING_OFFSET_IN_PIXELS = 400;
const VERTICAL_PARENT_LAZY_LOADING_OFFSET_IN_PIXELS = 200;
const NOT_LOADED_CLASS_NAME = 'not-loaded';
const DATA_SOURCE_ATTR_NAME = 'data-src';
const DATA_TIMEOUT_ATTR_NAME = 'data-timeout';

type ScrollDirection = 'vertical' | 'horizontal';

interface ObserverDetails {
  observerId: string;
  dataSourceAttrName?: string;
  notLoadedClassName?: string;
  verticalScroll?: {
    scrollElement: HTMLElement;
    imagesContainerElement?: HTMLElement;
    lazyLoadingOffsetInPixels?: number;
  };
  horizontalScroll?: {
    parentScrollElement: HTMLElement;
    horizontalScrollElement: HTMLElement;
    imagesContainerElement?: HTMLElement;
    lazyLoadingOffsetInPixels?: number;
  };
}

@Injectable({ providedIn: 'root' })
export class LazyLoadingImagesService {
  private renderer: Renderer2;
  private observers = new Map<string, { observer?: IntersectionObserver; parentObserver?: IntersectionObserver }>();

  constructor(
    private windowService: WindowService,
    private platformService: PlatformService,
    private rendererFactory: RendererFactory2,
    private domCtrl: DomController,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public initializeObserver(props: ObserverDetails): void {
    if (!this.windowService.isWindowDefined) {
      return;
    }

    props.verticalScroll ? this.initializeVerticalScrollObserver(props) : this.initializeHorizontalObserver(props);
  }

  public disconnectObserver(observerId: string): void {
    if (this.observers.has(observerId)) {
      const current = this.observers.get(observerId);
      current.observer?.disconnect();
      current.parentObserver?.disconnect();
      this.observers.delete(observerId);
    }
  }

  private initializeVerticalScrollObserver({
    observerId,
    verticalScroll,
    dataSourceAttrName = DATA_SOURCE_ATTR_NAME,
    notLoadedClassName = NOT_LOADED_CLASS_NAME,
  }: ObserverDetails): void {
    const imagesContainerElement = verticalScroll.imagesContainerElement ?? verticalScroll.scrollElement;
    const verticalLazyLoadingOffsetInPixels = verticalScroll.lazyLoadingOffsetInPixels ?? VERTICAL_LAZY_LOADING_OFFSET_IN_PIXELS;

    if (!imagesContainerElement) {
      return;
    }

    const lazyLoadingImages = Array.from(imagesContainerElement.querySelectorAll('.lazy-loading-image'))
      .map((n) => n as HTMLElement)
      .filter((imageElement) => (imageElement ? imageElement.classList?.contains(notLoadedClassName) : false));

    if (!lazyLoadingImages?.length) {
      return;
    }

    if (this.observers.has(observerId)) {
      this.disconnectObserver(observerId);
    }

    if (lazyLoadingImages?.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const isIntersecting = entry.isIntersecting || entry.intersectionRatio > 0;

            isIntersecting
              ? this.maybeLoadImageWithDelay({
                  scrollDirection: 'vertical',
                  observer,
                  entry,
                  dataSourceAttrName,
                  notLoadedClassName,
                  verticalLazyLoadingOffsetInPixels,
                })
              : this.cancelLoadingImageWithDelay(entry);
          });
        },
        {
          root: verticalScroll.scrollElement ?? this.windowService.window.document,
          rootMargin: `${verticalLazyLoadingOffsetInPixels}px 0px ${verticalLazyLoadingOffsetInPixels}px`,
          threshold: 0,
        },
      );

      lazyLoadingImages.forEach((imageElement) => observer.observe(imageElement));

      this.observers.set(observerId, { observer });
    }
  }

  private initializeHorizontalObserver({
    observerId,
    horizontalScroll,
    dataSourceAttrName = DATA_SOURCE_ATTR_NAME,
    notLoadedClassName = NOT_LOADED_CLASS_NAME,
  }: ObserverDetails): void {
    const imagesContainerElement = horizontalScroll.imagesContainerElement ?? horizontalScroll.horizontalScrollElement;
    const horizontalLazyLoadingOffsetInPixels = horizontalScroll.lazyLoadingOffsetInPixels ?? HORIZONTAL_LAZY_LOADING_OFFSET_IN_PIXELS;

    if (!imagesContainerElement) {
      return;
    }

    if (this.observers.has(observerId)) {
      this.disconnectObserver(observerId);
    }

    // We need to check first if the horizontal scroller container is visible in the page
    // since they are probably rendered in a vertical scroller container
    const parentObserver = new IntersectionObserver(
      (parentEntries) => {
        parentEntries.forEach((parentEntry) => {
          const isParentIntersecting = parentEntry.isIntersecting || parentEntry.intersectionRatio > 0;

          if (isParentIntersecting) {
            void this.platformService.wait(IMAGE_LOADING_DELAY).then(() => {
              const boundingClientRect = parentEntry.target.getBoundingClientRect();

              const isStillIntersecting = this.isStillVisibleVertically(
                boundingClientRect.top,
                boundingClientRect.bottom,
                VERTICAL_PARENT_LAZY_LOADING_OFFSET_IN_PIXELS,
              );

              if (isStillIntersecting && !this.observers.get(observerId)?.observer) {
                const lazyLoadingImages = Array.from(imagesContainerElement.querySelectorAll('.lazy-loading-image'))
                  .map((n) => n as HTMLElement)
                  .filter((imageElement) => (imageElement ? imageElement.classList?.contains(notLoadedClassName) : false));

                if (!lazyLoadingImages?.length) {
                  return;
                }

                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      const isIntersecting = entry.isIntersecting || entry.intersectionRatio > 0;

                      isIntersecting
                        ? this.maybeLoadImageWithDelay({
                            scrollDirection: 'horizontal',
                            observer,
                            entry,
                            dataSourceAttrName,
                            notLoadedClassName,
                            horizontalLazyLoadingOffsetInPixels,
                          })
                        : this.cancelLoadingImageWithDelay(entry);
                    });
                  },
                  {
                    root: horizontalScroll.horizontalScrollElement ?? this.windowService.window.document,
                    rootMargin: `0px ${horizontalLazyLoadingOffsetInPixels}px 0px`,
                    threshold: 0,
                  },
                );

                lazyLoadingImages.forEach((imageElement) => observer.observe(imageElement));
                this.observers.get(observerId).observer = observer;
              }
            });
          } else if (isParentIntersecting) {
            console.log('=====> ALREADY CREATED!!!');
          }
        });
      },
      {
        root: horizontalScroll.parentScrollElement ?? this.windowService.window.document,
        rootMargin: `${VERTICAL_PARENT_LAZY_LOADING_OFFSET_IN_PIXELS}px 0px ${VERTICAL_PARENT_LAZY_LOADING_OFFSET_IN_PIXELS}px`,
        threshold: 0,
      },
    );

    parentObserver.observe(horizontalScroll.horizontalScrollElement);
    this.observers.set(observerId, { parentObserver });
  }

  private isStillVisibleVertically(top: number, bottom: number, offset: number): boolean {
    return top <= this.windowService.window.innerHeight + offset && bottom >= 0 - offset;
  }

  private isStillVisibleHorizontally(left: number, right: number, offset: number): boolean {
    return left <= this.windowService.window.innerWidth + offset && right >= 0 - offset;
  }

  private maybeLoadImageWithDelay({
    scrollDirection,
    observer,
    entry,
    dataSourceAttrName,
    notLoadedClassName,
    verticalLazyLoadingOffsetInPixels = VERTICAL_LAZY_LOADING_OFFSET_IN_PIXELS,
    horizontalLazyLoadingOffsetInPixels = HORIZONTAL_LAZY_LOADING_OFFSET_IN_PIXELS,
  }: {
    scrollDirection: ScrollDirection;
    observer: IntersectionObserver;
    entry: IntersectionObserverEntry;
    dataSourceAttrName: string;
    notLoadedClassName: string;
    verticalLazyLoadingOffsetInPixels?: number;
    horizontalLazyLoadingOffsetInPixels?: number;
  }): void {
    let timeoutId = this.getDataTimeoutValue(entry.target);

    if (timeoutId) {
      return;
    }

    timeoutId = this.windowService.window.setTimeout(() => {
      // Calling getBoundingClientRect() on each element is a very expensive task because
      // it causes layout/reflows so one small improvement is to check if the element was
      // visible. If not, we don't need to worry about this image. If the image was visible,
      // we do need to call the getBoundingClientRect() again to see if the image is still
      // visible (to handle scenarios where the user is scrolling very fast and images that
      // were visible 100ms ago are not visible anymore and should not be loaded).

      const wasVisible =
        scrollDirection === 'vertical'
          ? this.isStillVisibleVertically(entry.boundingClientRect.top, entry.boundingClientRect.bottom, verticalLazyLoadingOffsetInPixels)
          : this.isStillVisibleHorizontally(
              entry.boundingClientRect.left,
              entry.boundingClientRect.right,
              HORIZONTAL_LAZY_LOADING_OFFSET_IN_PIXELS,
            );

      if (wasVisible) {
        const boundingClientRect = entry.target.getBoundingClientRect();

        const isStillVisible =
          scrollDirection === 'vertical'
            ? this.isStillVisibleVertically(boundingClientRect.top, boundingClientRect.bottom, verticalLazyLoadingOffsetInPixels)
            : this.isStillVisibleHorizontally(boundingClientRect.left, boundingClientRect.right, horizontalLazyLoadingOffsetInPixels);

        if (isStillVisible) {
          const imageUrl = entry.target.getAttribute(dataSourceAttrName);

          if (imageUrl) {
            void this.platformService
              .preloadImage(imageUrl, 0)
              .then(() => this.domCtrl.write(() => this.renderer.removeClass(entry.target, notLoadedClassName)));
          }

          this.deleteDataTimeoutValue(entry.target);
          observer.unobserve(entry.target);
        }
      }
    }, IMAGE_LOADING_DELAY);

    this.setDataTimeoutValue(entry.target, timeoutId);
  }

  private cancelLoadingImageWithDelay(entry: IntersectionObserverEntry): void {
    const timeoutId = this.getDataTimeoutValue(entry.target);

    if (!timeoutId) {
      return;
    }

    this.windowService.window.clearTimeout(timeoutId);
    this.deleteDataTimeoutValue(entry.target);
  }

  private getDataTimeoutValue(element: Element): number {
    if (!element) {
      return null;
    }

    const dataTimeout = element.getAttribute(DATA_TIMEOUT_ATTR_NAME);
    return dataTimeout ? +dataTimeout : null;
  }

  private setDataTimeoutValue(element: Element, timeout: number): void {
    this.domCtrl.write(() => {
      element.setAttribute(DATA_TIMEOUT_ATTR_NAME, timeout.toString());
    });
  }

  private deleteDataTimeoutValue(element: Element): void {
    this.domCtrl.write(() => {
      element.removeAttribute(DATA_TIMEOUT_ATTR_NAME);
    });
  }
}
